import React from "react";

import WorkPeriod from "../../Types/WorkPeriod";

const defaultPeriodNames = ["Avond", "Middag", "Ochtend"];
const isDefaultPeriodName = (name: string) =>
    defaultPeriodNames.some((defaultName) => defaultName.toLowerCase() === name.trim().toLowerCase());

interface Props {
    open: boolean;
    title: string;
    workDayDate: string;
    period: WorkPeriod | null;
    onClose: () => void;
    onSave: (period: WorkPeriod) => void;
}

interface State {
    name: string;
    isCustomName: boolean;
    cashTip: string;
    pinTip: string;
}

export default class WorkPeriodEditorDialog extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            name: defaultPeriodNames[0],
            isCustomName: false,
            cashTip: "0.00",
            pinTip: "0.00",
        };
    }

    public componentDidMount() {
        this.syncFromProps();
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.period !== this.props.period || prevProps.open !== this.props.open) {
            this.syncFromProps();
        }
    }

    private syncFromProps() {
        const period = this.props.period;
        if (period == null) return;
        const periodName = period.Name?.trim() ?? "";
        const hasCustomName = periodName !== "" && !isDefaultPeriodName(periodName);

        this.setState({
            name: periodName || defaultPeriodNames[0],
            isCustomName: hasCustomName,
            cashTip: (period.TipCash ?? 0).toFixed(2),
            pinTip: (period.TipPin ?? 0).toFixed(2),
        });
    }

    private isValidAmount(value: string) {
        return /^(0|[1-9]\d*)([.,]\d{0,2})?$/.test(value);
    }

    private parseAmount(value: string) {
        return Number((value || "0").replace(",", ".")) || 0;
    }

    private save = () => {
        const { period, workDayDate } = this.props;
        if (period == null) return;
        if (!this.state.name.trim()) return;
        if (!this.isValidAmount(this.state.cashTip) || !this.isValidAmount(this.state.pinTip)) return;

        this.props.onSave(
            new WorkPeriod(
                period.Id,
                this.state.name.trim(),
                workDayDate,
                this.parseAmount(this.state.pinTip),
                this.parseAmount(this.state.cashTip),
                period.Workers ?? [],
            ),
        );
    };

    public render() {
        if (!this.props.open || this.props.period == null) return null;

        const accent = "#c79656";
        const textPrimary = "#0f172a";
        const textMuted = "rgba(0,0,0,0.62)";
        const borderSoft = "rgba(0,0,0,0.10)";
        const isCustomName = this.state.isCustomName;
        const nameChipStyle = (selected: boolean) => ({
            padding: "7px 11px",
            borderRadius: 999,
            border: selected ? "1px solid " + accent : "1px solid " + borderSoft,
            background: selected ? "rgba(199,150,86,0.14)" : "#fff",
            color: selected ? "#7c5a31" : textPrimary,
            fontSize: 12,
            fontWeight: 800,
            cursor: "pointer",
        });

        return (
            <div
                onClick={this.props.onClose}
                style={{
                    position: "fixed",
                    width: "100%",
                    inset: 0,
                    background: "rgba(10,12,16,0.45)",
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 16,
                    zIndex: 1000,
                }}
            >
                <div
                    onClick={(event) => event.stopPropagation()}
                    style={{
                        width: "min(560px, 92vw)",
                        background: "linear-gradient(180deg, #ffffff 0%, #fbfbfb 100%)",
                        borderRadius: 16,
                        boxShadow: "0 30px 60px rgba(0,0,0,0.25)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "14px 16px",
                            background: "linear-gradient(180deg, rgba(199,150,86,0.10), rgba(199,150,86,0.04))",
                            borderBottom: "1px solid " + borderSoft,
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 8, height: 22, borderRadius: 8, background: accent }} />
                            <div style={{ color: textPrimary, fontWeight: 800 }}>
                                {this.props.title}
                            </div>
                        </div>
                        <button
                            onClick={this.props.onClose}
                            aria-label="Close"
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: 10,
                                border: "1px solid " + borderSoft,
                                background: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                            }}
                        >
                            ✕
                        </button>
                    </div>

                    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span style={{ fontSize: 12, color: textMuted, fontWeight: 700 }}>Name</span>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 2 }}>
                                {defaultPeriodNames.map((name) => {
                                    const selected = !isCustomName && this.state.name.trim().toLowerCase() === name.toLowerCase();

                                    return (
                                        <button
                                            key={name}
                                            type="button"
                                            onClick={() => this.setState({ name, isCustomName: false })}
                                            style={nameChipStyle(selected)}
                                        >
                                            {name}
                                        </button>
                                    );
                                })}
                                <button
                                    type="button"
                                    onClick={() => this.setState({ name: "", isCustomName: true })}
                                    style={nameChipStyle(isCustomName)}
                                >
                                    Other
                                </button>
                            </div>
                            {isCustomName && (
                                <input
                                    value={this.state.name}
                                    onChange={(event) => this.setState({ name: event.target.value })}
                                    placeholder="e.g. Lunch shift"
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                        borderRadius: 10,
                                        border: "1px solid " + borderSoft,
                                        background: "#fff",
                                        boxSizing: "border-box",
                                        color: textPrimary,
                                    }}
                                />
                            )}
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <span style={{ fontSize: 12, color: textMuted, fontWeight: 700 }}>Cash tip</span>
                                <input
                                    value={this.state.cashTip}
                                    onChange={(event) => {
                                        const nextValue = event.target.value;
                                        if (nextValue === "" || this.isValidAmount(nextValue)) {
                                            this.setState({ cashTip: nextValue });
                                        }
                                    }}
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                        borderRadius: 10,
                                        border: "1px solid " + borderSoft,
                                        background: "#fff",
                                        boxSizing: "border-box",
                                    }}
                                />
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <span style={{ fontSize: 12, color: textMuted, fontWeight: 700 }}>PIN tip</span>
                                <input
                                    value={this.state.pinTip}
                                    onChange={(event) => {
                                        const nextValue = event.target.value;
                                        if (nextValue === "" || this.isValidAmount(nextValue)) {
                                            this.setState({ pinTip: nextValue });
                                        }
                                    }}
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                        borderRadius: 10,
                                        border: "1px solid " + borderSoft,
                                        background: "#fff",
                                        boxSizing: "border-box",
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 8,
                            padding: 16,
                            borderTop: "1px solid " + borderSoft,
                            background: "#fff",
                        }}
                    >
                        <button
                            onClick={this.props.onClose}
                            style={{
                                padding: "10px 14px",
                                borderRadius: 10,
                                border: "1px solid " + borderSoft,
                                background: "#fff",
                                fontWeight: 700,
                                fontSize: 12,
                                cursor: "pointer",
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={this.save}
                            style={{
                                padding: "10px 14px",
                                borderRadius: 10,
                                border: "none",
                                background: accent,
                                color: "#fff",
                                fontWeight: 900,
                                fontSize: 12,
                                cursor: "pointer",
                                boxShadow: "0 6px 18px rgba(199,150,86,0.4)",
                            }}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
